import type { JSX } from 'react';
import type { WireArtifact } from '@logistics/contracts';
import { initials } from '@/lib/format';
import { Fact, StatusPill, Truncated, caption } from './parts';

type Driver = Extract<WireArtifact, { kind: 'driver' }>;
type Line = Driver['drivers'][number];

/**
 * One driver as a card, several as a list. A phone number and a mail address
 * are the provider's text and stay text: no `tel:` or `mailto:` is built from
 * a record.
 */
export function DriverArtifact({ artifact }: { artifact: Driver }): JSX.Element {
  const only = artifact.drivers.length === 1 ? artifact.drivers[0] : undefined;
  return (
    <div className="flex flex-col gap-2.5">
      {only !== undefined && <DriverCard line={only} />}
      {only === undefined && artifact.drivers.map((line, i) => <DriverRow key={i} line={line} />)}
      {artifact.drivers.length === 0 && <p className="text-mute text-[13px]">No drivers.</p>}
      {artifact.truncated && <Truncated what={`${artifact.drivers.length} drivers`} />}
    </div>
  );
}

function DriverCard({ line }: { line: Line }): JSX.Element {
  const facts = [
    { k: 'Phone', v: line.phone },
    { k: 'Email', v: line.email },
    { k: 'Truck', v: line.assignedTruck },
  ].filter((f): f is { k: string; v: string } => f.v !== undefined);
  return (
    <div className="flex flex-col gap-3.5">
      <div className="flex items-center justify-between gap-3">
        <span className="min-w-0 text-xl font-semibold tracking-[-.01em] [overflow-wrap:anywhere]">
          {line.name}
        </span>
        {line.status !== undefined && <StatusPill status={line.status} />}
      </div>
      {facts.length > 0 && (
        <div className="grid grid-cols-2 gap-2.5">
          {facts.map((f) => (
            <Fact key={f.k} k={f.k} v={f.v} />
          ))}
        </div>
      )}
    </div>
  );
}

function DriverRow({ line }: { line: Line }): JSX.Element {
  const detail = line.assignedTruck ?? line.phone ?? line.email;
  return (
    <div className="border-line bg-surf flex items-center gap-3 rounded-xl border px-3.5 py-2.5">
      <span className="bg-surf2 grid h-6 w-[34px] flex-none place-items-center rounded-md font-mono text-[10px] font-medium">
        {initials(line.name)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[12.5px] font-medium">{line.name}</div>
        {detail !== undefined && (
          <div className={`${caption} mt-0.5 truncate`}>{detail.toUpperCase()}</div>
        )}
      </div>
      {line.status !== undefined && (
        <span className="text-mute max-w-[88px] flex-none truncate text-[11.5px]">
          {line.status}
        </span>
      )}
    </div>
  );
}
